import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import BottomSheet from '@perpay-web/components/base/BottomSheet/BottomSheet';
import { useAnimatedModal } from '@perpay-web/components/base/Modal/useAnimatedModal';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import { AWS_WAF_API_KEY } from '@perpay-web/fintech/constants/serviceCredentials';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from './Captcha.scss';

const Captcha = ({
    className = '',
    showWafCaptcha = false,
    onSuccessCallback,
}) => {
    const containerRef = useRef();
    const navigate = useNavigate();

    const { isModalOpen, setIsModalOpen } = useModal(showWafCaptcha);
    const {
        isAnimatedModalOpen,
        setIsAnimatedModalOpen,
        animatedModalClassName,
    } = useAnimatedModal({ isModalOpen, setIsModalOpen });

    useEffect(() => {
        if (showWafCaptcha) {
            setIsAnimatedModalOpen(true);
            const onSuccess = () => {
                onSuccessCallback();
                setIsAnimatedModalOpen(false);
            };

            window.AwsWafCaptcha.renderCaptcha(containerRef.current, {
                apiKey: AWS_WAF_API_KEY,
                onSuccess,
                dynamicWidth: true,
                disableLanguageSelector: true,
                onLoad: () => {
                    // Find and access the shadow root
                    const captchaElement =
                        document.querySelector('awswaf-captcha');
                    if (captchaElement && captchaElement.shadowRoot) {
                        // Adopt the stylesheet to bypass the default styles embedded in the shadow root
                        const sheet = new CSSStyleSheet();
                        sheet.replaceSync(`
                            .btn.btn-primary {
                                box-sizing: border-box;
                                outline: none;
                                display: flex;
                                flex-direction: row;
                                justify-content: center;
                                align-items: center;
                                border: 1px solid transparent;
                                width: 100%;
                                height: 50px;
                                border-radius: 5px;
                                font-size: 16px;
                                line-height: 21px;
                                background-color: #2e71ea;
                                color: #fff;
                                margin-top: 16px;
                            }
                            .btn.btn-primary:focus {
                                outline: none !important; /* necessary to override value embedded in the shadow root style property */
                                box-shadow: none;
                            }
                            .btn.btn-primary:focus-visible {
                                box-shadow: 0 0 0 0.2em rgba(#2e71ea, 0.3);
                            }
                            .btn.btn-primary:hover {
                                background-color: #437fec;
                            }
                            .btn.btn-primary:active {
                                background-color: #2864cd;
                            }
                            .btn.btn-primary:disabled {
                                opacity: 1;
                                background-color: #c7cbce;
                                color: #587088;
                            }

                            div:has(> #amzn-btn-verify-internal),
                            div:has(> #amzn-btn-refresh-internal),
                            div:has(> div > #amzn-btn-verify-internal) {
                                flex-wrap: wrap;
                                width: 100%;
                            }

                            div {
                                overflow: hidden;
                            }

                        `);
                        captchaElement.shadowRoot.adoptedStyleSheets = [
                            ...captchaElement.shadowRoot.adoptedStyleSheets,
                            sheet,
                        ];
                    }
                },
            });
        }
    }, [navigate, onSuccessCallback, setIsAnimatedModalOpen, showWafCaptcha]);

    if (!isAnimatedModalOpen) {
        return null;
    }

    return (
        <BottomSheet
            className={getClassName(
                styles.modal,
                animatedModalClassName,
                className,
            )}
            contentClassName={styles['modal-content']}
            onClose={() => {
                setIsAnimatedModalOpen(false);
            }}
        >
            <div className={styles.wrapper}>
                <div id='aws-waf-captcha-container' ref={containerRef} />
            </div>
        </BottomSheet>
    );
};

export default Captcha;
